import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRoleDisplayName } from '../utils/helpers';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Clock, Users, BarChart3, LogOut, Cpu, BriefcaseBusiness, Building2, MapPin, Shield, CalendarRange, BookMarked, ClipboardCheck, CalendarDays, CalendarCheck, HeartHandshake, NotebookTabs, ScanFace, Cctv, Radar, Activity, Network, KeyRound, X
} from 'lucide-react';

const links = [
  { to: '/entities', icon: Network, label: 'Entities', platformOnly: true },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard.view', disallowRoles: ['student'] },
  { to: '/attendance',icon: Clock,           label: 'Attendance', permission: 'attendance.view', disallowRoles: ['student'] },
  { to: '/face-attendance', icon: ScanFace, label: 'Face Attendance', roleOnly: 'admin' },
  { to: '/live-tracking', icon: MapPin, label: 'Live Tracking', permission: 'readers.view', disallowRoles: ['student'] },
  { to: '/attendance-rules', icon: ClipboardCheck, label: 'Attendance Policies', permission: 'attendance.manage' },
  { to: '/holidays', icon: CalendarDays, label: 'Holidays', permission: 'holidays.view' },
  { to: '/leaves', icon: CalendarCheck, label: 'Leaves', permission: 'leave.view' },
  { to: '/users',     icon: Users,           label: 'Users', permission: 'users.view' },
  { to: '/departments', icon: Building2,     label: 'Departments', permission: 'departments.view' },
  { to: '/roles',    icon: Shield,           label: 'Roles', permission: 'roles.view' },
  { to: '/locations', icon: MapPin,          label: 'Locations', permission: 'locations.view' },
  { to: '/sections',  icon: BriefcaseBusiness, label: 'Teams', permission: 'sections.view' },
  { to: '/subjects',  icon: BookMarked,      label: 'Work Areas', permission: 'subjects.view' },
  { to: '/timetable', icon: CalendarRange,   label: 'Shifts', permission: 'timetable.view' },
  { to: '/readers',   icon: Cpu,             label: 'Readers', permission: 'readers.view' },
  { to: '/cameras',   icon: Cctv,            label: 'IP Cameras', permission: 'cameras.view' },
  { to: '/edge-agents', icon: Radar,          label: 'Camera Edge', permission: 'cameras.view' },
  { to: '/camera-analytics', icon: Activity,  label: 'Camera Detection', permission: 'cameras.view' },
  { to: '/reports',   icon: BarChart3,       label: 'Reports' },
  { to: '/my-children', icon: HeartHandshake, label: 'Linked People', permission: 'parent.view', roleOnly: 'parent' },
  { to: '/my-attendance', icon: NotebookTabs, label: 'My Attendance', permission: 'attendance.view', roleOnly: 'student' },
];

const Sidebar = () => {
  const { user, logout, hasPermission, entityCategory, terminology } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const terminologyLabels = {
    '/sections': terminology.groups,
    '/subjects': terminology.areas,
    '/timetable': terminology.schedules,
    '/my-children': terminology.linkedPeople,
  };
  const visibleLinks = links.filter((link) => {
    if (link.platformOnly && user?.is_platform_admin !== true) return false;
    if (link.roleOnly && user?.role !== link.roleOnly) {
      return false;
    }
    if (Array.isArray(link.disallowRoles) && link.disallowRoles.includes(user?.role)) {
      return false;
    }
    return !link.permission || hasPermission(link.permission);
  });
  const handleBrandRefresh = () => {
    window.location.reload();
  };

  const closePasswordForm = () => {
    if (changingPassword) return;
    setShowPasswordForm(false);
    setPasswordError('');
    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    setPasswordError('');
    if (passwordForm.new_password.length < 12 || passwordForm.new_password.length > 128) {
      setPasswordError('New password must contain 12-128 characters.');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    if (passwordForm.current_password === passwordForm.new_password) {
      setPasswordError('New password must be different from the current password.');
      return;
    }
    setChangingPassword(true);
    try {
      const response = await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      closePasswordForm();
      toast.success(response.data.message || 'Password changed. Sign in again.');
      logout();
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Unable to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
    <div className="fixed left-5 top-5 z-20 flex h-[calc(100vh-2.5rem)] w-64 flex-col overflow-hidden rounded-[2rem] border border-[rgba(31,44,58,0.08)] bg-[rgba(252,248,241,0.94)] text-slate-900 shadow-[0_28px_70px_rgba(90,76,61,0.18)] backdrop-blur-2xl">
      <div className="border-b border-[rgba(31,44,58,0.08)] p-6">
        <button
          type="button"
          onClick={handleBrandRefresh}
          className="flex w-full items-center gap-3 rounded-2xl text-left transition-transform hover:scale-[1.01]"
        >
          <img
            src="/logo192.png"
            alt="Smarten Attendance"
            className="h-14 w-14 rounded-2xl object-cover shadow-lg shadow-amber-900/20"
          />
          <div>
            <h1 className="text-[10px] font-semibold uppercase tracking-[0.28em] text-teal-500/70 leading-tight">Smarten Attendance</h1>
              <p className="text-[11px] text-slate-500">{terminology.operationsDescription}</p>
          </div>
        </button>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-5">
        {visibleLinks.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-2xl px-4 py-3 transition-all text-sm font-medium ${
                isActive
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-900/20'
                  : 'text-slate-500 hover:bg-white/60 hover:text-slate-900'
              }`
            }
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/70 text-slate-500 shadow-sm transition-colors group-hover:bg-white group-hover:text-slate-900">
              <Icon size={18} />
            </span>
            {terminologyLabels[to] || label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[rgba(31,44,58,0.08)] p-4">
        <div className="mb-3 rounded-2xl bg-white/45 px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">Signed In</p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-orange-500 text-sm font-bold">
            {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs capitalize text-slate-500">{getRoleDisplayName(user?.role, entityCategory)}</p>
              <p className="truncate text-[10px] text-teal-700">{user?.entity?.name || user?.entity?.code}</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowPasswordForm(true)}
          className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-[rgba(31,44,58,0.08)] bg-white/30 px-4 py-3 text-sm text-slate-500 transition-colors hover:bg-white/75 hover:text-slate-900"
        >
          <KeyRound size={16} />
          Change password
        </button>
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-2xl border border-[rgba(31,44,58,0.08)] bg-white/30 px-4 py-3 text-sm text-slate-500 transition-colors hover:bg-white/75 hover:text-slate-900"
        >
          <LogOut size={16} />
          Sign out
        </button>
        <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">
          Powered by <span className="text-slate-600">Induxen India Private Limited</span>
        </p>
      </div>
    </div>
      {showPasswordForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" role="presentation">
          <form
            onSubmit={submitPasswordChange}
            className="w-full max-w-md rounded-[2rem] border border-white/50 bg-[#fcf8f1] p-6 text-slate-900 shadow-2xl"
            aria-labelledby="change-password-title"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="change-password-title" className="text-xl font-semibold">Change password</h2>
                <p className="mt-1 text-sm text-slate-500">You will be signed out after the password changes.</p>
              </div>
              <button type="button" onClick={closePasswordForm} disabled={changingPassword} className="rounded-xl p-2 text-slate-500 hover:bg-white" aria-label="Close change password form">
                <X size={18} />
              </button>
            </div>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-medium">
                Current password
                <input type="password" autoComplete="current-password" required maxLength={128} value={passwordForm.current_password}
                  onChange={(event) => setPasswordForm({ ...passwordForm, current_password: event.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-teal-500" />
              </label>
              <label className="block text-sm font-medium">
                New password
                <input type="password" autoComplete="new-password" required minLength={12} maxLength={128} value={passwordForm.new_password}
                  onChange={(event) => setPasswordForm({ ...passwordForm, new_password: event.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-teal-500" />
                <span className="mt-1 block text-xs font-normal text-slate-500">Use 12-128 characters and do not reuse your current password.</span>
              </label>
              <label className="block text-sm font-medium">
                Confirm new password
                <input type="password" autoComplete="new-password" required minLength={12} maxLength={128} value={passwordForm.confirm_password}
                  onChange={(event) => setPasswordForm({ ...passwordForm, confirm_password: event.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-teal-500" />
              </label>
            </div>
            {passwordError && <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{passwordError}</p>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={closePasswordForm} disabled={changingPassword} className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium disabled:opacity-60">Cancel</button>
              <button type="submit" disabled={changingPassword} className="flex-1 rounded-xl bg-teal-600 px-4 py-3 text-sm font-medium text-white disabled:opacity-60">
                {changingPassword ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default Sidebar;
